import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Award, Upload, FileText, Calendar, CheckCircle, AlertCircle, Clock, Trash2, Download, X, Eye, Printer
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import StaffCeHours from '@/components/StaffCeHours';

import API from '@/config/api';
// API imported from config;

const QUALIFICATION_ICONS = {
  aht_training: '🧠',
  cpr_training: '❤️',
  mandated_reporter: '📋',
  high_school_diploma: '🎓',
  wi_registry: '📜',
  background_check: '✅'
};

const StaffQualifications = ({ staffId, staffName, onClose }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [qualifications, setQualifications] = useState({});
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [editingQual, setEditingQual] = useState(null);
  const [editForm, setEditForm] = useState({
    completed: false,
    completion_date: '',
    expiration_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchQualifications();
  }, [staffId]);

  const fetchQualifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/${staffId}/qualifications`, getAuthHeaders());
      setQualifications(response.data.qualifications);
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Failed to load qualifications');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQualification = async () => {
    if (!editingQual) return;
    
    try {
      await axios.put(
        `${API}/staff/${staffId}/qualifications/${editingQual}`,
        editForm,
        getAuthHeaders()
      );
      toast.success('Qualification updated');
      setEditingQual(null);
      fetchQualifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleToggleQualification = async (qualKey, currentStatus) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const qual = qualifications[qualKey];
      
      let expirationDate = null;
      if (qual.renewable && qual.renewal_years) {
        const expDate = new Date();
        expDate.setFullYear(expDate.getFullYear() + qual.renewal_years);
        expirationDate = expDate.toISOString().split('T')[0];
      }
      
      await axios.put(
        `${API}/staff/${staffId}/qualifications/${qualKey}`,
        {
          completed: !currentStatus,
          completion_date: !currentStatus ? today : null,
          expiration_date: !currentStatus ? expirationDate : null,
          notes: ''
        },
        getAuthHeaders()
      );
      toast.success(!currentStatus ? 'Qualification completed' : 'Qualification removed');
      fetchQualifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/staff/${staffId}/documents/${docType}`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Document uploaded');
      fetchQualifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API}/staff/${staffId}/documents/${docId}`, getAuthHeaders());
      toast.success('Document deleted');
      fetchQualifications();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const openEditDialog = (qualKey) => {
    const qual = qualifications[qualKey];
    setEditForm({
      completed: qual.completed || false,
      completion_date: qual.completion_date || '',
      expiration_date: qual.expiration_date || '',
      notes: qual.notes || ''
    });
    setEditingQual(qualKey);
  };

  const getStatusBadge = (qual) => {
    if (!qual.completed) {
      return <Badge variant="secondary">Not Completed</Badge>;
    }
    
    switch (qual.status) {
      case 'expired':
        return <Badge variant="destructive">Expired ({qual.days_expired} days)</Badge>;
      case 'expiring_soon':
        return <Badge variant="warning" className="bg-amber-500">Expiring Soon ({qual.days_until_expiration} days)</Badge>;
      case 'expiring_90_days':
        return <Badge variant="outline" className="border-amber-400 text-amber-600">Expires in {qual.days_until_expiration} days</Badge>;
      case 'valid':
        return <Badge variant="default" className="bg-green-600">Valid</Badge>;
      default:
        return <Badge variant="default" className="bg-green-600">Completed</Badge>;
    }
  };

  const getDocumentsForType = (docType) => {
    return documents.filter(d => d.type === docType);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const documentTypes = [
    { key: 'staff_record', name: 'Staff Record (DCF-F-CFS0053)' },
    { key: 'orientation_checklist', name: 'Orientation Checklist (DCF-F-CFS2026)' },
    { key: 'continuing_education', name: 'Continuing Education Record (DCF-F-CFS0053A)' },
    { key: 'health_report', name: 'Staff Health Report' },
    { key: 'aht_certificate', name: 'AHT Certificate' },
    { key: 'cpr_certificate', name: 'CPR Certificate' },
    { key: 'first_aid_certificate', name: 'First Aid Certificate' },
    { key: 'mandated_reporter_certificate', name: 'Mandated Reporter Certificate' },
    { key: 'diploma', name: 'Diploma/GED' },
    { key: 'transcripts', name: 'Transcripts' },
    { key: 'wi_registry_certificate', name: 'WI Registry Certificate' },
    { key: 'background_check_approval', name: 'Background Check Approval' },
    { key: 'other', name: 'Other Documents' }
  ];

  return (
    <div className="space-y-6">
      {/* Continuing Education hours - DCF 251.05(3) */}
      <StaffCeHours staffId={staffId} staffName={staffName} />

      {/* Qualifications Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            Training & Certifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {Object.entries(qualifications).map(([key, qual]) => (
              <div 
                key={key} 
                className={`p-4 rounded-lg border-2 transition-colors ${
                  qual.completed 
                    ? qual.status === 'expired' 
                      ? 'border-red-200 bg-red-50' 
                      : qual.status === 'expiring_soon'
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={qual.completed}
                      onCheckedChange={() => handleToggleQualification(key, qual.completed)}
                      data-testid={`qual-${key}`}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{QUALIFICATION_ICONS[key]}</span>
                        <span className="font-medium">{qual.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{qual.description}</p>
                      {qual.renewable && qual.renewal_years && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Renew every {qual.renewal_years} year(s)
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(qual)}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(key)}
                    >
                      <Calendar className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {qual.completed && qual.expiration_date && (
                  <div className="mt-2 pl-10 text-sm text-muted-foreground">
                    Completed: {qual.completion_date} • Expires: {qual.expiration_date}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Document Uploads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Document Uploads
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {documentTypes.map(({ key, name }) => {
              const docs = getDocumentsForType(key);
              return (
                <div key={key} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{name}</span>
                      {docs.length > 0 && (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {docs.length} file(s)
                        </Badge>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        id={`upload-${key}`}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={(e) => handleFileUpload(key, e.target.files[0])}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`upload-${key}`).click()}
                        disabled={uploadingDoc === key}
                      >
                        {uploadingDoc === key ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-1" />
                            Upload
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  {docs.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {docs.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span>{doc.original_filename}</span>
                            <span className="text-xs text-muted-foreground">
                              ({Math.round(doc.file_size / 1024)} KB)
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground mr-2">
                              {new Date(doc.uploaded_at).toLocaleDateString()}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(doc.url, '_blank')}
                              title="View document"
                            >
                              <Eye className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const printWindow = window.open(doc.url, '_blank');
                                if (printWindow) {
                                  printWindow.onload = () => printWindow.print();
                                }
                              }}
                              title="Print document"
                            >
                              <Printer className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                              title="Delete document"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Edit Qualification Dialog */}
      <Dialog open={!!editingQual} onOpenChange={() => setEditingQual(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit {editingQual && qualifications[editingQual]?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={editForm.completed}
                onCheckedChange={(checked) => setEditForm({...editForm, completed: checked})}
              />
              <Label>Completed</Label>
            </div>
            
            <div className="space-y-2">
              <Label>Completion Date</Label>
              <Input
                type="date"
                value={editForm.completion_date || ''}
                onChange={(e) => setEditForm({...editForm, completion_date: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Expiration Date</Label>
              <Input
                type="date"
                value={editForm.expiration_date || ''}
                onChange={(e) => setEditForm({...editForm, expiration_date: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setEditingQual(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateQualification}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffQualifications;
